import React, { useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    TextField,
    Button,
    Paper,
    Typography,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from "@mui/material";
import NavBar from "../navbar";
import { useNavigate } from "react-router-dom";
import config from "../config";
import * as Sentry from "@sentry/react";
import { fromUtcInput, toUtcInput } from "../utc";

const CreateToken = () => {
    const navigate = useNavigate();

    const [tokenData, setTokenData] = useState({
        name: "",
        points: 0,
        expires_at: "",
        user_id: "",
        plan_id: "",
        credit_source: "",
    });

    // Credit source options based on the enum
    const creditSourceOptions = [
        { value: "purchase", label: "Purchase" },
        { value: "trial", label: "Trial" },
        { value: "subscription", label: "Subscription" },
        { value: "internal", label: "Internal" },
    ];
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [newToken, setNewToken] = useState(null);
    const [newTokenID, setNewTokenID] = useState(null);

    const validate_logged_in = validateLoggedIn();
    if (validate_logged_in) {
        return validate_logged_in;
    }

    const handleChange = (field, value) => {
        setTokenData({ ...tokenData, [field]: value });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const trimmedName = tokenData.name.trim();
            if (!trimmedName) {
                setError("Name is required");
                setSaving(false);
                return;
            }
            if (trimmedName.length > 100) {
                setError("Name must be at most 100 characters");
                setSaving(false);
                return;
            }
            if (!tokenData.expires_at) {
                setError("Expires At is required");
                setSaving(false);
                return;
            }
            // Validate that credit_source is provided
            if (!tokenData.credit_source) {
                setError("Credits Source is required");
                setSaving(false);
                return;
            }

            const response = await fetch(config.apiBaseUrl + "/admin/tokens", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
                body: JSON.stringify({ ...tokenData, name: trimmedName }),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error: ${response.status} ${response.statusText} - ${errorText}`);
            }
            const data = await response.json();
            setNewToken(data.token);
            setNewTokenID(data.id);
        } catch (err) {
            Sentry.captureException(err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCloseDialog = () => {
        navigate("/tokens/" + newTokenID + "/edit");
    };

    return (
        <>
            <NavBar />
            <Box sx={{ maxWidth: 600, margin: "auto", padding: 3 }}>
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        Create Token
                    </Typography>
                    {error && (
                        <Typography variant="body2" color="error" gutterBottom>
                            {error}
                        </Typography>
                    )}
                    <TextField
                        fullWidth
                        margin="normal"
                        required
                        label="Name"
                        inputProps={{ maxLength: 100 }}
                        error={Boolean(error && error.startsWith("Name"))}
                        value={tokenData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Points"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={tokenData.points}
                        onChange={(e) => handleChange("points", Math.max(0, parseInt(e.target.value, 10) || 0))}
                    />
                    <Typography variant="body2" gutterBottom>
                        Expires At (UTC)
                    </Typography>
                    <TextField
                        fullWidth
                        margin="normal"
                        type="datetime-local"
                        value={toUtcInput(tokenData.expires_at)}
                        onChange={(e) => handleChange("expires_at", fromUtcInput(e.target.value, ""))}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="User ID"
                        value={tokenData.user_id}
                        onChange={(e) => handleChange("user_id", e.target.value)}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Plan ID"
                        value={tokenData.plan_id}
                        onChange={(e) => handleChange("plan_id", e.target.value)}
                    />
                    <FormControl 
                        fullWidth 
                        margin="normal"
                        required
                        error={error && error.includes("Credits Source")}
                    >
                        <InputLabel id="credits-source-label">Credits Source</InputLabel>
                        <Select
                            labelId="credits-source-label"
                            value={tokenData.credit_source}
                            label="Credits Source"
                            onChange={(e) => handleChange("credit_source", e.target.value)}
                        >
                            {creditSourceOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {error && error.includes("Credits Source") && (
                            <FormHelperText>{error}</FormHelperText>
                        )}
                    </FormControl>
                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => navigate(`/tokens`)}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Paper>
            </Box>
            {newToken && (
                <Dialog open={true} onClose={handleCloseDialog}>
                    <DialogTitle>Token Created</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            You will not be able to see this token again, please, save it and store carefully:
                        </DialogContentText>
                        <Box
                            sx={{
                                backgroundColor: "#f5f5f5",
                                padding: 2,
                                marginTop: 2,
                                borderRadius: 1,
                                wordBreak: "break-all",
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                {newToken}
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} color="primary" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

export default CreateToken;
